import { useState, useEffect } from "react";
import {
  GoabDivider,
  GoabButton,
  GoabButtonGroup,
  GoabModal,
  GoabTooltip,
} from "@abgov/react-components";
import { isEventTypeActive } from "../../../MPP-Common-SPA/utils/globalUtility";
import { trackEvent } from "../../../MPP-Common-SPA/utils/analytics";
import { FINISH_LATER_EVENT } from "../../../MPP-Common-SPA/utils/analytics-constants";

export const SaveAndFinishLaterButton = ({
  type = "tertiary",
  isLoggedIn,
  getResourceValue,
  templateForm,
  fields,
  context,
  portal,
  user,
  getFormValues,
  saveStepData,
  finishLater,
  onPrevious,
  currentStep,
  getRefData,
  programEventMapping,
  isProgramEventMappingLoading,
  isEventsEnabled,
  isActivePortal,
  selectedEvent,
}) => {
  const [showModal, setShowModal] = useState(false);

  const { renderer: { props } = {} } = currentStep ?? {};

  const {
    showBackButton,
    showStepCounter,
    backButtonLabel,
    hideFinishLaterButton,
    requireValidFormToFinishLater,
  } = props ?? {};

  // Trigger validation once on step load when requireValidFormToFinishLater is true to ensure templateForm.isValid is set
  useEffect(() => {
    if (requireValidFormToFinishLater && templateForm?.validateForm) {
      templateForm.validateForm();
    }
  }, []); // Empty dependency array - run only once on mount

  if (!isLoggedIn) {
    return null;
  }

  const handlePrevious = async () => {
    const formValues = getFormValues ? getFormValues() : {};

    await onPrevious(formValues, fields, context, getRefData);
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
  };

  const handleFinishLater = async () => {
    if (templateForm) {
      saveStepData({ ...(templateForm?.values ?? {}) });
    } else if (getFormValues) {
      saveStepData({ ...(getFormValues() ?? {}) });
    }
    let stepData = undefined;
    if (getFormValues) {
      stepData = { ...(getFormValues() ?? {}) };
    }
    await finishLater(stepData);

    // Send GA event when the finish later confirm/continue button is clicked. The label is the current step when the button is clicked.
    trackEvent(FINISH_LATER_EVENT, {
      finish_later_step: currentStep.name,
    });
    setShowModal(false);
  };

  // Wait for feature flag data to load
  if (isProgramEventMappingLoading) {
    return <></>;
  }

  const isActiveEventTypeFlag = selectedEvent
    ? isEventTypeActive(programEventMapping, selectedEvent, user)
    : false;

  if ((isEventsEnabled && !isActiveEventTypeFlag) || !isActivePortal) {
    return <></>;
  }

  return (
    <>
      {((showBackButton && showStepCounter) || !hideFinishLaterButton) && (
        <GoabDivider mt="2xl" mb="xl"></GoabDivider>
      )}

      <div
        className="step-nav-actions"
      >
        <GoabButtonGroup>
          {showBackButton && showStepCounter && (
            <GoabButton
              type="tertiary"
              leadingIcon="arrow-back"
              onClick={handlePrevious}
            >
              {getResourceValue({
                key: backButtonLabel
                  ? "RESOURCES@" + backButtonLabel
                  : "RESOURCES@global.button.previous",
              })}
            </GoabButton>
          )}
        </GoabButtonGroup>
        <GoabButtonGroup>
          {!hideFinishLaterButton &&
            (requireValidFormToFinishLater && !templateForm.isValid ? (
              <GoabTooltip
                position="top"
                hAlign="left"
                content={getResourceValue({
                  key: "RESOURCES@global.saveAndFinishLater.tooltip",
                })}
              >
                <GoabButton type={type} disabled="true">
                  {getResourceValue({
                    key: "RESOURCES@global.saveAndFinishLater",
                  })}
                </GoabButton>
              </GoabTooltip>
            ) : (
              <GoabButton type={type} onClick={() => setShowModal(true)}>
                {getResourceValue({
                  key: "RESOURCES@global.saveAndFinishLater",
                })}
              </GoabButton>
            ))}
        </GoabButtonGroup>
      </div>
      <GoabModal
        testId="saveAndFinishLater--modal"
        heading={getResourceValue({
          key: "RESOURCES@saveAndFinishLater.modal.header",
        })}
        role="alertdialog"
        open={showModal}
        actions={
          <GoabButtonGroup alignment="end">
            <GoabButton
              type="secondary"
              name="Back"
              testId="saveAndFinishLater-back-button"
              onClick={() => setShowModal(false)}
            >
              {getResourceValue({
                key: "RESOURCES@saveAndFinishLater.button.finishLater",
              })}
            </GoabButton>
            <GoabButton
              type="primary"
              name="continue"
              testId="saveAndFinishLater-continue-button"
              onClick={handleFinishLater}
            >
              {getResourceValue({
                key: "RESOURCES@saveAndFinishLater.button.continue",
              })}
            </GoabButton>
          </GoabButtonGroup>
        }
      >
        <p>
          {getResourceValue({
            key:
              "RESOURCES@" +
              (!user.isProxyUser
                ? "saveAndFinishLater.confirmation.text"
                : "saveAndFinishLater.proxyConfirmation.text"),
          })}
        </p>
      </GoabModal>
    </>
  );
};
